import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSIONS } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  StorageExclusionsSearchableKeywords,
  StorageProtectionExclusionsFormValue,
} from '../../../../utils/uiSettings/storageProtection/storage-protection.types';
import {
  getStorageExclusionsSearchableKeywords,
  STORAGE_PROTECTION_EXCLUSIONS_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/storageProtection/storage-protection.values';
import { GzStorageProtectionExclusionsGridWrapperWebComponent } from '../gridComponents/GzStorageProtectionExclusionsGridWebComponent';
import { StorageProtectionExclusionItem } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSIONS)
export class GzStorageProtectionExclusionsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  StorageProtectionExclusionsFormValue,
  StorageExclusionsSearchableKeywords
> {
  /**
   * Path for Storage Protection Exclusions section.
   */
  protected path = {
    moduleId: ModuleId.STORAGE_PROTECTION,
    sectionId: SectionId.EXCLUSIONS,
  };

  /**
   * Searchable keywords for Storage Protection Exclusions section.
   */
  protected searchableKeywords = getStorageExclusionsSearchableKeywords();

  @query('#storage-protection-exclusions-toggle')
  private storageExclusionsToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#storage-protection-exclusions-info-text')
  private storageExclusionsInfoText: HTMLElement;

  @query('#storage-protection-exclusions-content')
  private storageExclusionsContent: HTMLElement;

  @query('#storage-protection-exclusions-grid-form-field')
  private storageExclusionsGridFormField: GzFormFieldWebComponent<
    GzStorageProtectionExclusionsGridWrapperWebComponent,
    StorageProtectionExclusionItem[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="storage-protection-exclusions-toggle">
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[StorageExclusionsSearchableKeywords.StorageExclusions].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
        ><span
          >${this.searchableKeywords[StorageExclusionsSearchableKeywords.StorageExclusions].translatedLabel}</span
        ></gz-toggle
      >
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="storage-protection-exclusions-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'These exclusions apply to Security Servers when used as a scanning service for storage devices.'
      )}</gz-text
    >`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="storage-protection-exclusions-content">
        <gz-form-field id="storage-protection-exclusions-grid-form-field">
          <gz-policy-panel-storage-protection-exclusions-grid
            gz-form-input
          ></gz-policy-panel-storage-protection-exclusions-grid>
        </gz-form-field>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.storageProtection.exclusions
   */
  protected getValueFromManager(): StorageProtectionExclusionsFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.STORAGE_PROTECTION]: {
          [SectionId.EXCLUSIONS]: STORAGE_PROTECTION_EXCLUSIONS_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.STORAGE_PROTECTION]: {
          [SectionId.EXCLUSIONS]: PolicyPanelManager.getInstance().getPolicy('uiSettings.storageProtection.exclusions'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        storageProtection: {
          exclusions: {
            enabled: this.storageExclusionsToggle,
            exclusionItems: this.storageExclusionsGridFormField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        storageProtection: {
          exclusions: {
            enabled: this.valueT.uiSettings.storageProtection.exclusions.enabled,
            exclusionItems: this.valueT.uiSettings.storageProtection.exclusions.exclusionItems,
          },
        },
      },
    };

    // set policy id
    this.storageExclusionsGridFormField.Input.setPolicyId(PolicyPanelManager.getInstance().getPolicyId());
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'storageProtection', 'exclusions'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.storageExclusionsInfoText, !event.detail.checked);
    toggleDisabled(this.storageExclusionsContent, !event.detail.checked);

    // enable/disable tooltip
    const tooltip = this.storageExclusionsGridFormField.Input.shadowRoot?.getElementById('tooltip');
    if (event.detail.checked) {
      tooltip?.classList.remove('disabled');
    } else {
      tooltip?.classList.add('disabled');
    }
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.storageExclusionsGridFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSIONS: GzStorageProtectionExclusionsWebComponent;
  }
}
